import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Target, Book, Heart, Calendar, Brain } from 'lucide-react';

interface ProductSectionProps {
  onOpenModal?: () => void;
}

const ProductSection: React.FC<ProductSectionProps> = ({ onOpenModal }) => {
  const products = [
    {
      id: 1,
      icon: BookOpen,
      title: "Em Cada Palavra, Uma Semente",
      subtitle: "150 Bênçãos para Cultivar Fé, Amor e Virtude",
      image: "/images/capa em cada palavra uma semente.png",
      description: "Devocional com 150 bênçãos diárias para pais/cuidadores. Focado em estabelecer uma rotina sagrada: abençoar, refletir, ensinar e orar. Cultiva fé, amor, virtude e fortalece a identidade da criança.",
      tags: ["150 Bênçãos", "Para Pais", "Devocional", "Rotina Sagrada"],
      color: "from-blue-500 to-blue-600"
    },
    {
      id: 2,
      icon: Target,
      title: "Kit de Inteligência Emocional",
      subtitle: "Para Crianças (5 a 10 anos)",
      image: "/images/capa kiti inteligência emocional.png",
      description: "Kit lúdico para desenvolver Inteligência Emocional, ajudando a criança a nomear, compreender e gerenciar emoções. Fortalece autoestima, resiliência e fé.",
      tags: ["5-10 anos", "Cards & Jogos", "Emoções", "Autoestima"],
      color: "from-purple-500 to-purple-600"
    },
    {
      id: 3,
      icon: Book,
      title: "Meu Livro de Bênçãos e Aventuras",
      subtitle: "Descobrindo a Alegria de Caminhar com Deus",
      image: "/images/capa meu livro de bençãos e aventuras.png",
      description: "Livro interativo para crianças descobrirem a caminhada com Deus. Contém palavras de amor, atividades de desenho/escrita e desafios práticos focados em virtudes.",
      tags: ["4-8 anos", "Interativo", "Virtudes", "Atividades"],
      color: "from-green-500 to-green-600"
    },
    {
      id: 4,
      icon: Heart,
      title: "Meu Primeiro Guia de Oração",
      subtitle: "Diário Cheio de Fé, Amor e Gratidão",
      image: "/images/capa meu primeiro guia de oração.png",
      description: "Diário para a criança aprender a conversar com Deus diariamente, como um amigo. Inclui orações em versinhos para momentos específicos e espaços para desenho/escrita.",
      tags: ["5-10 anos", "Diário", "Oração", "Gratidão"],
      color: "from-pink-500 to-pink-600"
    },
    {
      id: 5,
      icon: Calendar,
      title: "Planner das Emoções",
      subtitle: "Planejador de Felicidade, Objetivos e Autocuidado",
      image: "/images/capa planner das emocoes.png",
      description: "Planejador que incentiva a reflexão e o estabelecimento de metas para o crescimento pessoal. Ajuda a criança a construir hábitos de saúde mental e praticar gratidão.",
      tags: ["7-13 anos", "Planner", "Metas", "Autocuidado"],
      color: "from-orange-500 to-orange-600"
    },
    {
      id: 6,
      icon: Brain,
      title: "Mindful Journal",
      subtitle: "Diário da Presença - Respire & Escreva",
      image: "/images/capa - mindful journal_.png",
      description: "Journal focado na prática de Mindfulness. Ajuda a criança a se conectar com pensamentos e sensações através de técnicas de aterramento e reflexão.",
      tags: ["10+ anos", "Mindfulness", "Reflexão", "Presença"],
      color: "from-indigo-500 to-indigo-600"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  // Funções de interação podem ser adicionadas aqui quando necessário

  return (
    <section className="py-20" style={{ backgroundColor: '#F7F5EF' }}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nossos Materiais
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Cada material foi cuidadosamente criado para cultivar fé, amor e virtude no coração das crianças.
          </p>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="bg-white rounded-3xl p-8 shadow-sm border border-gray-200"
            >
              {/* Product Image */}
              <div className="text-center mb-6">
                <div className="w-full aspect-square rounded-2xl overflow-hidden shadow-lg mb-4">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-contain bg-white"
                  />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                {product.title}
              </h3>

              {/* Subtitle */}
              <p className="text-sm text-gray-600 mb-4 text-center">
                {product.subtitle}
              </p>

              {/* Description */}
              <p className="text-gray-700 text-sm leading-relaxed mb-6 text-left">
                {product.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6 justify-center">
                {product.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: '#FFE89A',
                      color: '#1F2937'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Informações adicionais podem ser adicionadas aqui */}
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-200 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              🌱 Quer todos os materiais juntos?
            </h3>
            <p className="text-gray-600 mb-6">
              Adquira o kit completo com todos os 6 materiais por um preço especial.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-black text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors duration-300"
            >
              Ver Kit Completo
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProductSection;
